import { useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Users, Download, CalendarBlank } from "@phosphor-icons/react";

const monthNow = () => new Date().toISOString().slice(0, 7);

export default function TeamComparisonDialog({ open, onOpenChange }) {
  const [month, setMonth] = useState(monthNow());
  const [title, setTitle] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) {
      setMonth(monthNow());
      setTitle("");
    }
  }, [open]);

  const handleExport = async () => {
    if (!/^\d{4}-\d{2}$/.test(month)) {
      toast.error("Pick a valid month (YYYY-MM).");
      return;
    }
    setDownloading(true);
    try {
      const res = await http.post(
        "/tasks/report/team-comparison-pdf",
        { month, title: title.trim() || null },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Studio-Summary-${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Studio summary downloaded.");
      onOpenChange?.(false);
    } catch (e) {
      let msg = formatApiError(e);
      try {
        if (e?.response?.data instanceof Blob) {
          const t = await e.response.data.text();
          const parsed = JSON.parse(t);
          if (parsed?.detail) msg = typeof parsed.detail === "string" ? parsed.detail : msg;
        }
      } catch { /* keep */ }
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="team-comparison-dialog"
        className="max-w-md w-[96vw]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={22} weight="duotone" className="text-indigo-700" />
            Studio Summary — Side-by-Side
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-zinc-600 mt-1">
          A single-page PDF showing every active member's task counts for the
          month — Total, Complete, In Progress, To Do, Milestones delivered and
          their value, with a completion-% bar. Members with zero tasks for the
          period are hidden.
        </p>

        <div className="grid grid-cols-1 gap-3 mt-3">
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">
              <CalendarBlank size={12} weight="bold" className="inline mr-1" />
              Month
            </label>
            <Input
              type="month"
              data-testid="team-comparison-month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">
              Report Title (optional)
            </label>
            <Input
              data-testid="team-comparison-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`Studio Summary — ${month}`}
            />
          </div>
        </div>

        <DialogFooter className="mt-5">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="team-comparison-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={downloading}
            data-testid="team-comparison-submit"
            className="bg-indigo-700 hover:bg-indigo-800 text-white gap-2"
          >
            <Download size={16} weight="bold" />
            {downloading ? "Preparing PDF…" : "Export PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
